import React, { useMemo, useState } from 'react';
import { categorizeDlr } from '@paradigm/permissions/utils/humanizeDlr';
import { PermissionSummary } from '@paradigm/permissions/components';
import YamlPreview from './YamlPreview';
import '@paradigm/permissions/components/permissions.css';
import './DLRCallbackPreview.css';

/**
 * DLRCallbackPreview — 1:1 miniature replica of the Authorization page.
 *
 * Uses <PermissionSummary /> from the shared permission components for the
 * permission display — same visual language as the real /authorize page.
 */
function DLRCallbackPreview({ appName = 'Your App', userName = 'User', dlrInputs = [], inputParameters = [], schemaGrants = null, schemas = [], plugins = [], yamlPreview = '' }) {
  const [viewMode, setViewMode] = useState('visual'); // 'visual' | 'code'
  const schemaMap = useMemo(() => {
    const m = {};
    for (const s of schemas) {
      if (s.id) m[String(s.id)] = s;
      const uri = s.schema_name || s.uri;
      if (uri) m[uri] = s;
    }
    return m;
  }, [schemas]);

  const resolveSchemaName = (idOrUri) => {
    const s = schemaMap[String(idOrUri)];
    const name = s?.schema_name || s?.uri;
    if (!name) return String(idOrUri);
    return name.replace(/^raw:/, '');
  };

  // Convert DLR items into the standard { data, capabilities } shape
  const categorized = useMemo(
    () => categorizeDlr(dlrInputs, schemas, null, null),
    [dlrInputs, schemas],
  );

  const hasAnything = dlrInputs.length > 0 || inputParameters.length > 0;
  const hasDlr = dlrInputs.length > 0;
  const initial = appName.charAt(0).toUpperCase();

  // ── Empty state ──
  if (!hasAnything) {
    return (
      <div className="dlr-preview">
        <div className="dlr-preview__header-label">Authorization Preview</div>
        <div className="dlr-preview__v3-entity">
          <div className="dlr-preview__v3-entity-icon">{initial}</div>
          <div className="dlr-preview__v3-entity-text">
            <div className="dlr-preview__v3-entity-title">{appName} ↔ {userName}</div>
          </div>
        </div>
        <div className="dlr-preview__empty">
          <div className="dlr-preview__empty-text">Nothing is being requested from the user</div>
        </div>
        <div className="dlr-preview__actions">
          <span className="dlr-preview__btn dlr-preview__btn--deny">Deny</span>
          <span className="dlr-preview__btn dlr-preview__btn--approve">Approve</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dlr-preview">
      <div className="dlr-preview__header-row">
        <div className="dlr-preview__header-label">Authorization Preview</div>
        {yamlPreview && (
          <div className="dlr-preview__view-tabs">
            <button type="button" className={`dlr-preview__view-tab${viewMode === 'visual' ? ' active' : ''}`} onClick={() => setViewMode('visual')}>Visual</button>
            <button type="button" className={`dlr-preview__view-tab${viewMode === 'code' ? ' active' : ''}`} onClick={() => setViewMode('code')}>Code</button>
          </div>
        )}
      </div>

      {/* ── 1. Entity card ── */}
      <div className="dlr-preview__v3-entity">
        <div className="dlr-preview__v3-entity-icon">{initial}</div>
        <div className="dlr-preview__v3-entity-text">
          <div className="dlr-preview__v3-entity-title">{appName} ↔ {userName}</div>
          <div className="dlr-preview__v3-entity-desc">Review what this app is requesting</div>
        </div>
      </div>

      {/* ── Code view ── */}
      {viewMode === 'code' && yamlPreview && (
        <div className="dlr-preview__rse-wrapper">
          <YamlPreview yaml={yamlPreview} title="DLR YAML" collapsible={false} defaultExpanded />
        </div>
      )}

      {/* ── 2. Permission summary — same visual as the real consent page ── */}
      {viewMode === 'visual' && hasDlr && (
        <div className="dlr-preview__rse-wrapper">
          <PermissionSummary
            data={categorized.data}
            capabilities={categorized.capabilities}
          />
        </div>
      )}

      {/* ── 4. Variables mock (visual mode only) ── */}
      {viewMode === 'visual' && inputParameters.length > 0 && (
        <div className="dlr-preview__vars-section">
          <div className="dlr-preview__vars-header">Variables</div>
          <div className="dlr-preview__vars-hint">Fill in the values requested by {appName}.</div>
          {inputParameters.map((param, i) => {
            const pType = param.type || 'text';
            return (
              <div key={i} className="dlr-preview__var-row">
                <label className="dlr-preview__var-label">
                  {param.label || param.key}
                  {param.required && <span className="dlr-preview__var-req">*</span>}
                </label>
                {pType === 'nodes' ? (
                  <div className="dlr-preview__var-input dlr-preview__var-input--select">
                    <span>{param.max === 1 ? 'Select a node...' : 'Select nodes...'}</span>
                    {param.schema && <span className="dlr-preview__var-schema-tag">{resolveSchemaName(param.schema)}</span>}
                    <span className="dlr-preview__var-chevron">&#x25BE;</span>
                  </div>
                ) : pType === 'tag' ? (
                  <div className="dlr-preview__var-input dlr-preview__var-input--select">
                    <span>Select a tag...</span>
                    <span className="dlr-preview__var-chevron">&#x25BE;</span>
                  </div>
                ) : pType === 'schema' ? (
                  <div className="dlr-preview__var-input dlr-preview__var-input--select">
                    <span>Select a schema...</span>
                    <span className="dlr-preview__var-chevron">&#x25BE;</span>
                  </div>
                ) : pType === 'select' ? (
                  <div className="dlr-preview__var-input dlr-preview__var-input--select">
                    <span>Select...</span>
                    <span className="dlr-preview__var-chevron">&#x25BE;</span>
                  </div>
                ) : pType === 'timestamp' ? (
                  <div className="dlr-preview__var-input">
                    <span>Pick date/time...</span>
                  </div>
                ) : pType === 'boolean' ? (
                  <div className="dlr-preview__var-input dlr-preview__var-input--bool">
                    <span className="dlr-preview__var-checkbox-mock" />
                    <span>{param.label || param.key}</span>
                  </div>
                ) : pType === 'file' ? (
                  <div className="dlr-preview__var-input">
                    <span>Choose file...</span>
                  </div>
                ) : (
                  <div className="dlr-preview__var-input">
                    <span>Enter {param.label || param.key}...</span>
                  </div>
                )}
                {pType === 'nodes' && (param.min !== undefined || param.max !== undefined) && (
                  <div className="dlr-preview__var-constraint">
                    {param.min !== undefined && `min: ${param.min}`}
                    {param.min !== undefined && param.max !== undefined && ', '}
                    {param.max !== undefined && `max: ${param.max}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── 5. Action buttons mock ── */}
      <div className="dlr-preview__actions">
        <span className="dlr-preview__btn dlr-preview__btn--deny">Deny</span>
        <span className="dlr-preview__btn dlr-preview__btn--approve">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
          Approve
        </span>
      </div>
    </div>
  );
}

export default DLRCallbackPreview;
